import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

// Pagina ponte post-pagamento Stripe.
// Caricata nel Custom Tab (Android) / SFSafariViewController (iOS) dopo il checkout,
// oppure nel browser se l'utente apre il link di pagamento (es. da email).
// Desktop: redirect trasparente alla pagina di successo reale.
// Android (app): redirect ticinowild:///… → Custom Tab si chiude → appUrlOpen + Browser.close().
// iOS (app): nessun redirect allo schema custom (Browser.close() non è affidabile su SFSafariVC);
//   messaggio + chiusura manuale → browserFinished + pending localStorage (DeepLinkHandler).

export function buildSuccessPath(searchParams) {
  const type = searchParams.get("type");
  if (type === "shop_order") {
    const orderId = searchParams.get("orderId") || "";
    return `product-purchase-success?orderId=${encodeURIComponent(orderId)}`;
  }
  if (type === "connect_onboarding") {
    const companyId = searchParams.get("companyId") || "";
    const stripeAccountId = searchParams.get("stripeAccountId") || "";
    if (searchParams.get("link_expired") === "1") {
      return `company-details?companyId=${encodeURIComponent(companyId)}`;
    }
    const stripeStatus = searchParams.get("stripeStatus") || "verified";
    return `company-details?companyId=${encodeURIComponent(companyId)}&stripeAccountId=${encodeURIComponent(stripeAccountId)}&stripeStatus=${encodeURIComponent(stripeStatus)}`;
  }
  // activity_request (e fallback): stesso schema usato da UserPurchaseResult / DeepLinkHandler
  const sid = searchParams.get("session_id") || "";
  const rid = searchParams.get("requestId") || "";
  return `request/success?session_id=${encodeURIComponent(sid)}&requestId=${encodeURIComponent(rid)}`;
}

/** iOS / iPad nel browser in-app (es. SFSafariVC): UA classico oppure iPadOS che si spaccia per Mac. */
function isIosLikeDevice() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  if (/iPad|iPhone|iPod/i.test(ua)) return true;
  if (navigator.userAgentData?.platform === "iOS") return true;
  if (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1) return true;
  return false;
}

function isAndroidUserAgent(ua) {
  return /Android/i.test(ua || "");
}

export default function StripeReturn() {
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();

  function getCloseHint() {
    const ua = navigator.userAgent || "";
    return isIosLikeDevice()
      ? t("StripeReturn.closeHintIos")
      : isAndroidUserAgent(ua)
        ? t("StripeReturn.closeHintAndroid")
        : t("StripeReturn.closeHintDesktop");
  }

  useEffect(() => {
    const successPath = buildSuccessPath(searchParams);
    const ua = navigator.userAgent || "";

    if (!isIosLikeDevice() && !isAndroidUserAgent(ua)) {
      window.location.replace(`/${successPath}`);
      return;
    }

    if (isAndroidUserAgent(ua)) {
      window.location.replace(`ticinowild:///${successPath}`);
      return;
    }

    // iOS: nessun redirect — l'utente chiude manualmente; browserFinished completa il flusso.
  }, [searchParams]);

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <svg style={styles.icon} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="11" stroke="#225424" strokeWidth="2" />
          <path d="M7 12.5l3.5 3.5 6-7" stroke="#225424" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <h1 style={styles.title}>{t("StripeReturn.title")}</h1>
        <p style={styles.hint}>{getCloseHint()}</p>
      </div>
    </div>
  );
}

const styles = {
  container: {
    position: "fixed",
    inset: 0,
    zIndex: 9999,
    backgroundColor: "#ffffff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "24px",
    fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
  },
  card: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "16px",
    maxWidth: "360px",
    width: "100%",
    textAlign: "center",
  },
  icon: {
    width: "72px",
    height: "72px",
  },
  title: {
    margin: 0,
    fontSize: "26px",
    fontWeight: "700",
    color: "#225424",
  },
  hint: {
    margin: 0,
    fontSize: "15px",
    fontWeight: "400",
    color: "#225424",
    opacity: 0.65,
    lineHeight: "1.5",
  },
};
